import { useSortable } from "@dnd-kit/sortable";
import { differenceInMinutes } from "date-fns";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { Icon, Image, Label, Popup } from "semantic-ui-react";

import { AppointmentStatusCell, DATE_FORMATS_FNS, useCan } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { DAYPLANNER_COLUMNS } from "modules/Dayplanner";
import "modules/Dayplanner/components/AppointmentCard/AppointmentCard.scss";
import { CarQuickView, InterventionQuickView, NotificationIcons } from "modules/Dayplanner/components/AppointmentCard/components";
import { PATHS } from "router/paths";
import { classNames, getInitials } from "utils/common";
import { formatDate, formatDurationFromMinutes } from "utils/dateHelperFunctions";

import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import type { User } from "models";
import type { DayplannerAppointment, MechanicWithAppointments } from "modules/Dayplanner";
import type { CSSProperties } from "react";

type AppointmentCardProps = {
  appointment: DayplannerAppointment;
  column: DAYPLANNER_COLUMNS;
  ref?: React.Ref<HTMLElement>;
  mechanic?: MechanicWithAppointments;
  dndListeners?: SyntheticListenerMap;
  dndAttributes?: DraggableAttributes;
  isColumnCollapsed?: boolean;
};

type SortableAppointmentCardProps = {
  column: DAYPLANNER_COLUMNS;
  mechanic: MechanicWithAppointments;
  appointment: DayplannerAppointment;
  isColumnCollapsed?: boolean;
  onOver: (mechanicId: number) => void;
};

type DroppableAppointmentCardProps = {
  column: DAYPLANNER_COLUMNS;
  appointment: DayplannerAppointment;
  isColumnCollapsed?: boolean;
};

type AppointmentContentProps = {
  column: DAYPLANNER_COLUMNS;
  appointment: DayplannerAppointment;
  dndListeners: SyntheticListenerMap;
  dndAttributes: DraggableAttributes;
  mechanic?: MechanicWithAppointments;
};

type MechanicInfoProps = {
  column: DAYPLANNER_COLUMNS;
  appointment: DayplannerAppointment;
  mechanic?: MechanicWithAppointments | User;
};

const MechanicInfo = ({ appointment, mechanic, column }: MechanicInfoProps) => {
  const { t } = useTranslation();

  if (!mechanic) return null;

  const content = (
    <div>
      <span>
        {mechanic.first_name} {mechanic.last_name}
      </span>
      {column === DAYPLANNER_COLUMNS.Unassigned && (
        <>
          <div>{`${t("v8_start_time", "Start time")}: ${formatDate(appointment.planning_work_start ?? new Date(), DATE_FORMATS_FNS.dateMonthYearTime)}`}</div>
          <div>{`${t("v8_end_time", "End time")}: ${formatDate(appointment.planning_work_stop ?? new Date(), DATE_FORMATS_FNS.dateMonthYearTime)}`}</div>
        </>
      )}
    </div>
  );

  const trigger = mechanic.profile_picture ? (
    <Image src={mechanic.profile_picture} avatar />
  ) : (
    <div className="initials-fallback">{getInitials(mechanic.first_name, mechanic.last_name)}</div>
  );

  return <Popup content={content} trigger={trigger} position="top right" />;
};

const AppointmentContent = ({ appointment, mechanic, column, dndAttributes, dndListeners }: AppointmentContentProps) => {
  const history = useHistory();

  const { selectedLocation } = useSelectedLocation();

  if (!selectedLocation) return;

  const isDraggable = [DAYPLANNER_COLUMNS.Unassigned, DAYPLANNER_COLUMNS.Mechanics].includes(column);
  const planningWorkStop = appointment.car_return_time || appointment.planning_work_stop;
  const boardItemClassNames = classNames({
    "-bg-color-red": !!(planningWorkStop && differenceInMinutes(new Date(planningWorkStop), new Date()) <= 0),
    "-bg-color-yellow": !!(
      planningWorkStop &&
      differenceInMinutes(new Date(planningWorkStop), new Date()) <= 60 &&
      differenceInMinutes(new Date(planningWorkStop), new Date()) > 0
    ),
    "-appointment-internal": appointment.internal,
    "-panic": appointment.has_panic,
    "-inactive": column === DAYPLANNER_COLUMNS.Mechanics && appointment.inactive
  });

  const customerWaitingClassNames = classNames({
    "customer-waiting": Boolean(column === DAYPLANNER_COLUMNS.Unassigned && appointment.customer_waiting && appointment.car_in_shop && !appointment.car_out_of_shop)
  });
  const totalLaborMinutes = appointment.interventions?.reduce((acc, curr) => acc + (curr.customer_ok ? curr.labor_minutes || 0 : 0), 0);

  const handleCarDetailLink = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(`${PATHS.CAR_DETAIL}/${appointment.car_id}`);
  };

  const handleAppointmentDetailsLink = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(`${PATHS.APPOINTMENTS}/${appointment.id}`);
  };

  const getStackedMechanicStyle = (idx: number): CSSProperties | undefined => {
    if (!appointment.workingOn) return;

    const workingMechanics = appointment.workingOn.length;
    const OFFSET_PER_MECHANIC = 8;

    const position = workingMechanics - idx;

    return { position: "relative", zIndex: position, transform: `translateX(${position * OFFSET_PER_MECHANIC}px)` };
  };

  return (
    <div className={`BoardItem ${boardItemClassNames}`}>
      <div className="BoardItem-appointment-status -expanded">
        <AppointmentStatusCell
          dndListeners={dndListeners}
          dndAttributes={dndAttributes}
          showMoveCursor={isDraggable}
          value={{
            id: appointment.appointment_status_identifier,
            car_out_of_shop: appointment.car_out_of_shop,
            customcom_status: appointment.customer_communication?.status,
            car_check_paused_at: appointment.car_check_paused_at
          }}
          statuses={selectedLocation.statuses}
        />

        {appointment.kiosk_label_number && (
          <Label className="BoardItem-kiosk-label">
            <Icon className="tag" />
            <span>{appointment.kiosk_label_number}</span>
          </Label>
        )}
      </div>

      <div className="BoardItem-info -cursor-move">
        <CarQuickView
          appointment={appointment}
          popupTrigger={
            <span className="-cursor-pointer" onClick={handleCarDetailLink}>
              {appointment.reg_number ? `${appointment.reg_number}` : ""}
            </span>
          }
        />

        {appointment.reg_number ? " - " : ""}

        <InterventionQuickView
          appointment={appointment}
          selectedLocation={selectedLocation}
          popupTrigger={
            <span className="-cursor-pointer wo-nr" onClick={handleAppointmentDetailsLink}>
              {appointment.wo_nr}
            </span>
          }
        />
      </div>

      <div className="BoardItem-last-update">
        {planningWorkStop && <span className={customerWaitingClassNames}>{formatDate(planningWorkStop, DATE_FORMATS_FNS.dateMonthHoursMinutes)}</span>}
        {totalLaborMinutes ? (
          <span className="labor-minutes">
            <Icon className="clock" />
            {formatDurationFromMinutes(totalLaborMinutes)}
          </span>
        ) : null}
      </div>

      <div className="BoardItem-bottom-elements">
        <div className="Mechanics-Notifications-Container">
          <NotificationIcons appointment={appointment} />

          {column === DAYPLANNER_COLUMNS.Mechanics && appointment.workingOn && appointment.workingOn.length > 0 && (
            <div className={`Working-On-Mechanics ${appointment.workingOn.length >= 3 ? "margin-right-10" : "initials-fallback-margin-right"}`}>
              {appointment.workingOn.map((mechanic, idx) => (
                <div style={appointment.workingOn && appointment.workingOn.length >= 3 ? getStackedMechanicStyle(idx) : {}} key={mechanic.id}>
                  <MechanicInfo appointment={appointment} mechanic={mechanic} column={column} />
                </div>
              ))}
            </div>
          )}
        </div>

        {[DAYPLANNER_COLUMNS.Unassigned, DAYPLANNER_COLUMNS.CarReady].includes(column) && (
          <div className="BoardItem-planning-mechanic">
            <div className="Mechanics-Notifications-Container">
              <div className="Working-On-Mechanics initials-fallback-margin-right margin-right-negative-8">
                <MechanicInfo appointment={appointment} mechanic={mechanic} column={column} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CollapsedAppointmentContent = ({ appointment, mechanic, column, dndAttributes, dndListeners }: AppointmentContentProps) => {
  const { selectedLocation } = useSelectedLocation();

  const isDraggable = [DAYPLANNER_COLUMNS.Unassigned, DAYPLANNER_COLUMNS.Mechanics].includes(column);
  const trigger = (
    <Link className="BoardItem-appointment-status -collapsed" to={`/appointments/${appointment.id}`} {...dndAttributes} {...dndListeners}>
      <AppointmentStatusCell
        showMoveCursor={isDraggable}
        value={{
          id: appointment.appointment_status_identifier,
          car_out_of_shop: appointment.car_out_of_shop,
          customcom_status: appointment.customer_communication?.status,
          car_check_paused_at: appointment.car_check_paused_at
        }}
        statuses={selectedLocation?.statuses}
      />
    </Link>
  );

  const content = (
    <div className="AppointmentCard BoardItem-appointment-status -expanded">
      <AppointmentContent appointment={appointment} mechanic={mechanic} column={column} dndAttributes={dndAttributes} dndListeners={dndListeners} />
    </div>
  );

  return <Popup basic hoverable size="large" trigger={trigger} content={content} style={{ padding: "0", width: "230px", boxShadow: "none", border: "none" }} />;
};

export const AppointmentCard = ({
  appointment,
  mechanic,
  column,
  isColumnCollapsed = false,
  dndAttributes = { role: "", tabIndex: 0, "aria-disabled": false, "aria-pressed": undefined, "aria-roledescription": "", "aria-describedby": "" },
  dndListeners = {}
}: AppointmentCardProps) => {
  const isMechanicColumn = column === DAYPLANNER_COLUMNS.Mechanics;

  if (appointment.inactive && !isMechanicColumn) return null;

  return (
    <div className={`AppointmentCard ${isMechanicColumn ? "-mechanic" : ""}`}>
      {isColumnCollapsed ? (
        <CollapsedAppointmentContent appointment={appointment} mechanic={mechanic} column={column} dndAttributes={dndAttributes} dndListeners={dndListeners} />
      ) : (
        <AppointmentContent appointment={appointment} mechanic={mechanic} column={column} dndAttributes={dndAttributes} dndListeners={dndListeners} />
      )}
    </div>
  );
};

export const MechanicAppointmentCard = ({ appointment, mechanic, onOver, ...rest }: SortableAppointmentCardProps) => {
  const canAssignWo = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DayplannerAppointmentAssign);
  const { attributes, isDragging, listeners, setNodeRef, transform, transition, isOver } = useSortable({
    id: appointment.id,
    disabled: !canAssignWo,
    data: {
      appointment: { id: appointment.id, assigned_mechanic: appointment.assigned_mechanic, assigned_mechanic_order: appointment.assigned_mechanic_order },
      mechanicId: mechanic.id,
      order: appointment.assigned_mechanic_order
    }
  });

  useEffect(() => {
    if (isOver) onOver(mechanic.id);
  }, [isOver]);

  const style = {
    transition,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : "none",
    opacity: isDragging ? 0.3 : 1
  };

  return (
    <div style={style} ref={setNodeRef}>
      <AppointmentCard dndListeners={listeners} dndAttributes={attributes} appointment={appointment} mechanic={mechanic} {...rest} />
    </div>
  );
};

export const UnassignedAppointmentCard = ({ appointment, ...rest }: DroppableAppointmentCardProps) => {
  const canAssignWo = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DayplannerAppointmentAssign);
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: appointment.id,
    disabled: !canAssignWo,
    data: { appointment: { id: appointment.id }, mechanicId: null, unassigned: true }
  });

  if (appointment.inactive) return null;

  const style = { transition, transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : "none", opacity: isDragging ? 0.3 : 1 };

  return (
    <div style={style} ref={setNodeRef}>
      <AppointmentCard dndListeners={listeners} dndAttributes={attributes} appointment={appointment} {...rest} />
    </div>
  );
};
